import { ChangeDetectionStrategy, Component, computed, ViewEncapsulation } from '@angular/core';
import { PlotTreemapLevelsOptions, SeriesTreemapOptions } from 'highcharts';
import { PbiChartTypes } from '../../../shared/chart/chart';
import { VisualFieldRoleType } from '../VisualFieldRoleType';
import { VisualFieldRoleTypeMap } from '../VisualFieldRoleTypeMap';
import { DataPoint, DataPointField } from '../VisualInteractions';
import { Rows } from '../data';
import { ChartSeries, ChartVisual, ChartVisualComponent, CreateSeriesOptions, SeriesField } from './ChartVisual.component';
import { HighchartsChart } from './base/PbirHighchartsChart.component';
import { Color } from '../../../shared/utils/Color';
import { TextStyle } from '../VisualStyle';

@Component({
  selector: 'pbir-visual__treemapChart',
  templateUrl: './ChartVisual.component.html',
  styleUrls: ['./ChartVisual.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TreemapVisualComponent
  extends ChartVisualComponent<
    SeriesTreemapOptions,
    TreemapVisual.Options,
    TreemapVisual.State,
    TreemapVisual.Style> {
  get chartType() { return 'treemap'; }

  get renderRowLimit() { return 200; }

  get measuresRoleType() { return TreemapVisual.roles.measures; }
  get groupingRoleType() { return TreemapVisual.roles.group; }
  get legendRoleType() { return TreemapVisual.roles.details; }

  
  override createAllSeries(o: CreateSeriesOptions): ChartSeries<SeriesTreemapOptions>[] {
    let dataLabelsStyle = this.dataLabelsStyle();
    let dataLabelsFormatter = this.dataLabelPointFormatter();
    let dataLabelsEnabled = !!dataLabelsStyle?.enabled;
    
    let data: any[] = [];
    this.processRows(o, data, o.result.rows);

    let id = ChartVisual.getNextSeriesId();

    // INFO: Previously hasGrouping = (o.groupings.length + o.legends.length) > 0;
    // Because we emulate 'grouping' in measure only mode, we still want that single level added.
    let hasGrouping = true; 
    let hasLegend   = (o.groupings.length > 0) && (o.legends.length > 0);
    let levels: PlotTreemapLevelsOptions[] = [];

    if (hasGrouping) {
      levels.push({
        level: levels.length + 1,
        layoutAlgorithm: 'squarified',
        layoutStartingDirection: 'vertical',
        
        borderWidth: 0,
        
        ...<any>{
          allowPointSelect: true,
          inactiveOtherPoints: true,
        },

        dataLabels: {
          enabled: dataLabelsEnabled,
          padding: 4,
          posistion: 'left',
          align: 'left',
          verticalAlign: 'top',
          labelrank: 1,
        },
      });
    }

    levels.push({
      level: levels.length + 1,
      layoutAlgorithm: 'squarified',
      layoutStartingDirection: 'horizontal',
      
      //borderColor: this.style().backgroundColor(),
      borderWidth: 0.5,
      
      dataLabels: {
        enabled: dataLabelsEnabled,
        padding: 4,
        position: 'left',
        align: 'left',
        verticalAlign: 'bottom',
        labelrank: 2,
      }
    });

    let series: ChartSeries<SeriesTreemapOptions> = {
      type: 'treemap',
      id,
      name: `treemap${id}`,

      cluster: {
        enabled: true
      },

      animation: false,
      allowTraversingTree: false,
      alternateStartingDirection: false,
      nodeSizeBy: 'group',
      
      levelIsConstant: true,
      levels,
      data,
      allowPointSelect: true,

      ...<any>{
        allowPointSelect: true,
        inactiveOtherPoints: true,
      },

      dataLabels: {
        ...this.createDataLabelsOptions(),

        enabled: dataLabelsEnabled,
        useHTML: false,
        shadow: false,
        padding: 0,
        shape: null,
        crop: true,
        allowOverlap: false,
        defer: true,
        borderWidth: 0,
        overflow: null,
        nullFormat: '#NULL',
        className: 'pbir-visual__chart-data-label',

        style: {
          ...TextStyle.toHighchartsStyle(dataLabelsStyle),
          textOutline: 'none',
        },

        zIndex: 100,

        formatter: function (options) {
          return dataLabelsFormatter(this, options);
        },

        nullFormatter: function (options) {
          return '#NULL';
        },
      }
    };

    return [series];
  }

  processRows(o: CreateSeriesOptions, data: Highcharts.PointOptionsObject[], rows: Rows) {
    let groupings = [...o.groupings, ...o.legends];
    let measures  = [...o.measures];

    let hasGrouping  = groupings.length > 0;
    let hasLegend    = o.groupings?.length > 0 && o.legends?.length > 0;
    let measuresOnly = !hasGrouping;

    let root = new TreemapNode(this, o, ROOT_NODE_KEY, null);

    for (let row of rows) {
      let node = root;

      if (groupings.length) {
        for (let g of groupings) {
          let key = o.formatter.getValueData(g.field, row[g.dataIndex]);
          node = node.addChild(key, g);
        }

        for (let m of measures) {
          let value = o.formatter.getValueData(m.field, row[m.dataIndex]);
          node.setValue(m, value);
        }
      }
      else {
        for (let m of measures) {
          let value = o.formatter.getValueData(m.field, row[m.dataIndex]);
          let g = node.addChild(value, m, { customName: m.field.shortTitle });
          g.setValue(m, value);
        }
      }
    }

    let allValuesZeroOrLess = !root.anyValuesAboveZero();

    let nextColorIndex = 0;

    for (let g of root.children.values()) {
      let color = HighchartsChart.getColorByIndex(nextColorIndex++);

      let colorRgb = Color.parseCssValue(color);
      let borderColorRgb = Color.adjustBrightness(Color.adjustSaturation(colorRgb, -0.4), 0.2);
      let borderColor = Color.toHex(borderColorRgb);

      data.push({
        id:    g.id,
        name:  g.name,
        color,
        borderColor,
      });

      if (hasLegend) {
        for (let l of g.children.values()) {
          for (let m of l.values.values()) {
            data.push({
              parent: g.id,
              name:   l.name,
              value:  allValuesZeroOrLess ? m.absValue : m.value,
              custom:
                this.createSeriesPointCustomData(
                  m.dataPoint,
                  g.id,
                  groupings,
                  [g.name, l.name],
                  m.field.field,
                  m.valueDisplay,
                  null),
              borderColor,
            });
          }
        }
      }
      else {
        for (let m of g.values.values()) {
          data.push({
            parent: g.id,
            name:   m.field.field.shortTitle,
            value:  allValuesZeroOrLess ? m.absValue : m.value,
            custom:
              this.createSeriesPointCustomData(
                m.dataPoint,
                g.id,
                groupings,
                [g.name],
                m.field.field,
                m.valueDisplay,
                null),
            borderColor,
          });
        }
      }
    }
  }


  computeShowNameInDataLabel() {
    return this.groupingFields().length > 0
       && (this.legendFields().length > 0 || this.measureFields().length > 1);
  }


  override computePlotOptions(): Highcharts.PlotOptions {
    return {
      ...super.computePlotOptions(),
    };
  }

  override computeXAxis() {
    return undefined;
  }

  override computeYAxis() {
    return undefined;
  }

  override computeDataLabels() {
    return undefined;
  }
}

let nextNodeId = 1;

class TreemapNodeValue {
  constructor(
    readonly node:  TreemapNode,
    readonly field: SeriesField,
    readonly value: number) {
  }

  readonly belowZero = this.value < 0;
  readonly absValue = Math.abs(this.value);

  readonly valueDisplay = computed(() => {
    return this.node.options.formatter.getValueDisplay(this.field.field, this.value);
  });


  private _dataPoint: DataPoint = null;

  get dataPoint() {
    if (!this._dataPoint) {
      this._dataPoint = this.node.component.dataPoints.getValue(this.node.dataPoint, {
        field: this.field.field,
        value: this.value
      });
    }

    return this._dataPoint;
  }
}

const ROOT_NODE_KEY = {};

class TreemapNode {
  readonly children = new Map<any, TreemapNode>();
  readonly values = new Map<SeriesField, TreemapNodeValue>();

  constructor(
    readonly component: TreemapVisualComponent,
    readonly options: CreateSeriesOptions,
    readonly key: any,
    readonly field?: SeriesField,
    readonly parent?: TreemapNode,
    readonly customName?: string) { }

  get isRoot() {
    return this.key === ROOT_NODE_KEY;
  }

  readonly id = `${nextNodeId++}`;

  private _aboveZeroCount = 0;
  private _zeroOrLessCount = 0;

  get hasAboveZeroValues() { return this._aboveZeroCount > 0; }
  get hasZeroOrLessValues() { return this._zeroOrLessCount > 0; }
  
  anyValuesAboveZero() {
    return this.hasAboveZeroValues || [...this.children.values()].some(x => x.hasAboveZeroValues);
  }

  anyValuesZeroOrLess() {
    return this.hasZeroOrLessValues || [...this.children.values()].some(x => x.hasZeroOrLessValues);
  }

  addChild(key: any, field: SeriesField, options?: { customName: string }) {
    let child = this.children.get(key);
    if (!child) {
      child = new TreemapNode(this.component, this.options, key, field, this, options?.customName);
      this.children.set(key, child);
    }

    return child;
  }

  setValue(field: SeriesField, value: number) {
    if (value > 0)
      this._aboveZeroCount++;
    else
      this._zeroOrLessCount++;

    this.values.set(
      field,
      new TreemapNodeValue(this, field, value));
  }
  
  visitChildren(visitor: (node: TreemapNode) => void) {
    for (let child of this.children.values()) {
      visitor(child);
      child.visitChildren(visitor);
    }
  }

  private _name: string;

  get name() {
    return this._name ??= (this.customName ?? this.options.formatter.getValueDisplay(this.field.field, this.key));
  }

  private _dataPoint: DataPoint = null;

  get dataPoint() {
    if (!this._dataPoint) {
      let dataPointFields: DataPointField[] = [];

      for (let n: TreemapNode = this; n.key !== ROOT_NODE_KEY; n = n.parent) {
        dataPointFields.push({ field: n.field.field, value: n.key });
      }

      this._dataPoint = this.component.dataPoints.get(dataPointFields);
    }

    return this._dataPoint;
  }
}

export namespace TreemapVisual {
  export class State extends ChartVisual.State { }
  export class Style extends ChartVisual.Style { }

  export interface Options extends ChartVisual.Options {
  }

  export const roles = VisualFieldRoleTypeMap.create({
    group: new VisualFieldRoleType('group', {
      ...VisualFieldRoleType.Defaults.groupingGroup,
      limits: v => ({ min: 0, max: 1 }),
      sortable: false,
    }),

    details: new VisualFieldRoleType('details', {
      ...VisualFieldRoleType.Defaults.detailDetails,
      isEnabled: v => v.fieldRoles.get(roles.measures).fields.length <= 1,
      limits: v => ({ min: 0, max: 1 }),
      sortable: false,
    }),

    measures: new VisualFieldRoleType('measures', {
      ...VisualFieldRoleType.Defaults.measures,
      limits: v =>
        (v.fieldRoles.get(roles.details).fields.length > 0)
          ? { min: 1, max: 1 }
          : { min: 1 },
      sortable: false,
    }),
  });

  export const type = ChartVisual.defineType<TreemapVisualComponent>(
    TreemapVisualComponent,
    'treemap',
    roles,
    {
      visualOptions: {
        noXAxis: true,
        noYAxis: true,
        noLegend: true,

        dataLabelsEnabledByDefault: true,
      },
      createState: () => new State(),
      createStyle: () => new Style(),
      showOverlayUntilRendered: true,
      getPbiChartType: v => PbiChartTypes.treemap,
    });
}
